import { useEffect, useMemo, useRef, useState } from "react";
import { ModalShell } from "../../ModalShell";
import { EyeIcon, EyeOffIcon } from "../icons/EyeIcons";
import {
  SECURE_NOTE_EXPIRY_OPTIONS,
  SECURE_NOTE_MAX_VALUE_BYTES,
  type SecureNoteExpiryDays,
} from "../../../hooks/useSecureNotes";
import dialogStyles from "../../../pages/admin/AdminFeatureFlags.module.css";
import styles from "./SecureNoteModals.module.css";

const EXPIRY_LABELS: Record<SecureNoteExpiryDays, string> = {
  7: "7 days (default)",
  3: "3 days",
  1: "1 day",
};

const byteLength = (value: string): number => new TextEncoder().encode(value).length;

type CreateSecureNoteModalProps = {
  onClose: () => void;
  onCreate: (
    name: string,
    value: string,
    expiryDays: SecureNoteExpiryDays,
  ) => Promise<{ ok: boolean; message: string }>;
};

export const CreateSecureNoteModal = ({ onClose, onCreate }: CreateSecureNoteModalProps): JSX.Element => {
  const [name, setName] = useState<string>("");
  const [value, setValue] = useState<string>("");
  const [expiryDays, setExpiryDays] = useState<SecureNoteExpiryDays>(7);
  const [show, setShow] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => nameRef.current?.focus(), []);

  // The server limit is UTF-8 BYTES (SSM's 4096-byte cap), not characters —
  // count the same way so multi-byte input can't pass the counter and then
  // 400 on the server.
  const valueBytes = useMemo(() => byteLength(value), [value]);
  const overLimit = valueBytes > SECURE_NOTE_MAX_VALUE_BYTES;
  const canSubmit = name.trim().length > 0 && value.length > 0 && !overLimit && !submitting;
  const titleId = "create-secure-note-title";

  return (
    <ModalShell density="compact" onClose={onClose} titleId={titleId} size="sm" width="min(520px, 92vw)">
      <header className={dialogStyles.dialogHeader}>
        <h3 className={dialogStyles.dialogTitle} id={titleId}>New secure note</h3>
        <button type="button" className={dialogStyles.closeBtn} onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>

      <form
        className={styles.form}
        onSubmit={async (e) => {
          e.preventDefault();
          if (!canSubmit) return;
          setSubmitting(true);
          setError(null);
          const result = await onCreate(name.trim(), value, expiryDays);
          setSubmitting(false);
          if (!result.ok) setError(result.message);
        }}
      >
        <label className={styles.field}>
          <span className={styles.label}>Name</span>
          <input
            ref={nameRef}
            type="text"
            className={styles.input}
            value={name}
            maxLength={100}
            placeholder="e.g. Xero API key"
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError(null);
            }}
            disabled={submitting}
            required
          />
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Secret value</span>
          <div className={styles.valueWrap}>
            <textarea
              className={show ? styles.valueArea : `${styles.valueArea} ${styles.valueMasked}`}
              value={value}
              placeholder="Paste the secret here"
              onChange={(e) => {
                setValue(e.target.value);
                if (error) setError(null);
              }}
              autoComplete="off"
              spellCheck={false}
              disabled={submitting}
              required
              data-testid="secure-note-value"
            />
            <button
              type="button"
              className={styles.revealBtn}
              onClick={() => setShow((prev) => !prev)}
              aria-label={show ? "Hide secret value" : "Show secret value"}
              aria-pressed={show}
              disabled={submitting}
              tabIndex={-1}
            >
              {show ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
          <span className={styles.metaRow}>
            <span className={overLimit ? `${styles.counter} ${styles.counterOver}` : styles.counter}>
              {valueBytes} / {SECURE_NOTE_MAX_VALUE_BYTES} bytes
            </span>
          </span>
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Expires after</span>
          <select
            className={styles.select}
            value={expiryDays}
            onChange={(e) => setExpiryDays(Number(e.target.value) as SecureNoteExpiryDays)}
            disabled={submitting}
          >
            {SECURE_NOTE_EXPIRY_OPTIONS.map((days) => (
              <option key={days} value={days}>
                {EXPIRY_LABELS[days]}
              </option>
            ))}
          </select>
        </label>

        <p className={styles.hint}>
          Only our team can read this, and you can&apos;t view it again after saving. It deletes
          itself when it expires, or you can delete it sooner.
        </p>

        {error && <p className={styles.errorBanner}>{error}</p>}

        <footer className={styles.footer}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className={styles.primaryBtn} disabled={!canSubmit}>
            {submitting ? "Saving…" : "Save secure note"}
          </button>
        </footer>
      </form>
    </ModalShell>
  );
};
